// RNWY 2.0 · P2 — the venue model captured on an RNWY proposal, serialized to Proposal.RnwyVenuesJson.
// A job-level section (entered once — covers the whole job) + an UNBOUNDED list of venues, each
// carrying its system size + a quantity per CFG catalog item. The pricing engine (P3) reads this
// × the CFG rate card. This is INPUT ONLY — no prices here; the engine derives all pricing.

export interface RnwyVenue {
  name: string;
  systemSizeKey: string; // matches a CFG systemSizes[].key (xl/lg/md/sm/flex)
  quantities: Record<string, number>; // catalogKey -> qty
  notes: Record<string, string>; // catalogKey -> brand/model text (optional)
  // item 864 (Chad) — free-text notes PER SECTION (category name → note), a channel for the
  // customer (or anyone building) to talk to the reviewing team: "tell us anything about this
  // section". Stored verbatim in the venues JSON; visible to Pulse in the internal builder.
  sectionNotes?: Record<string, string>;
  // dart (2026-07-16, Chad) — per-venue, per-category chosen brand (category NAME -> brand). Options
  // come from the CFG category's brand list; prints in that category's scope heading. Stored verbatim
  // in the venue JSON (the backend never re-serializes it), so no C# change needed.
  categoryBrands?: Record<string, string>; // RETIRED (item 681) — replaced by `sources`; kept for old data.
  // item 681(E) (Chad) — the RNWY-controlled equipment this venue has, chosen from the CFG's
  // controlledEquipment list: category NAME -> selected product names. Auto-writes into the venue's
  // scope. Stored verbatim in the venue JSON (the backend never re-serializes it), so no C# change.
  controlledEquipment?: Record<string, string[]>;
  // item 681 (Chad) — the individual controllable SOURCES included at this venue, chosen from the CFG
  // "Sources" pool. Auto-writes into the venue's scope. Stored verbatim in the venue JSON.
  sources?: string[];
}

// item 653 — a job-level "special service" line: a one-off extra or an Items-catalog pick (SKU).
// `amount` is the LINE TOTAL (quantity × unitAmount) — the only field the pricing engine reads
// (it sums these into the one-time "special services" bucket). sku/quantity/unitAmount are display
// metadata that round-trip so the row rebuilds on reload (older MISC rows have label+amount only).
export interface RnwyJobMisc {
  label: string;
  amount: number;
  sku?: string;
  quantity?: number;
  unitAmount?: number;
  // dart (2026-07-14) — the catalog Item this add-on came from (Add-from-catalog). Kept so the
  // materialized invoice line links back to the SKU and resolves its own commission reasons.
  // Absent for a one-off typed line.
  itemId?: string;
}

// RNWY 2.0 · P5 — SwitchPilot switch counts, entered ONCE at the deal level (mirrors
// basix.da/Models/RnwyVenues.cs RnwySwitchPilotInputs). Priced into a separate, subscription-only
// SwitchPilot module — never folded into the three RNWY-license options.
export interface RnwySwitchPilotInputs {
  // item 645 — the GUIDED input: one total switch count (+ brand for scope). The engine derives
  // config + license (base covers 10, then flat tier-1 above 10). The legacy per-tier fields
  // below stay for older venues JSON (used only when switchCount is 0).
  switchCount: number;
  brand: string; // item 646 — for the generated scope text
  // item 654 — one-time SwitchPilot config extras via the SAME Items engine as proposal-level
  // add-ons. `specialItems` is the editable list; `specialFeatures` mirrors its rolled-up total
  // (kept in sync by the UI) so the engine keeps reading one $ (OneTimeConfig += SpecialFeatures).
  specialItems: RnwyJobMisc[];
  specialFeatures: number; // one-time special-features $ (= sum of specialItems)
  initQty: number;
  perSwitchQty: number;
  baseLicenseQty: number;
  tier1Qty: number;
  tier2Qty: number;
  tier3Qty: number;
  tier4Qty: number;
}

// item 647 — the RNWY module Yes/No toggles (include/exclude flags, NO pricing effect — the
// venue engine is the license). Concourse + Aviator default INCLUDED; SwitchPilot is the optional
// add-on (default OFF; toggling it on reveals the SwitchPilot pricing build). Shown on the PDF as
// a "System includes / excludes" summary.
export interface RnwyModules {
  concourse: boolean;
  aviator: boolean;
  switchPilot: boolean;
  // item 647 — Third-party integration: a generic non-RNWY include/exclude flag. Drives the
  // Statement-of-Work scope auto-gen + (later) the non-RNWY/Crestron platform field in Step 3.
  thirdParty: boolean;
}
export const emptyModules = (): RnwyModules => ({
  concourse: true, aviator: true, switchPilot: false, thirdParty: false,
});

// item 1040 (Chad) — the portal "Add hardware" pick: exactly ONE PRSPLY tier + a quantity
// locked to 1/3/5/7. Any qty above 1 IS high availability, automatically — the extra units
// are the redundancy, no separate toggle. Spec-only (no pricing in the portal); the pick
// rides the venues JSON into Pulse review, where the priced line is added like any hardware.
export interface RnwyJobHardware {
  sku: string;
  qty: number; // 1 | 3 | 5 | 7
}

// item 1040 — the three customer-facing tiers (locked catalog SKUs, seeded by
// RnwyCanonicalItemSeeder.HardwareTiers — keep in sync with it).
export const RNWY_HARDWARE_TIERS = [
  { sku: 'PRSPLY_T1_LITE', label: 'T1 Lite', desc: 'RNWY imaged SNUC — 4-core / 16GB / 256GB' },
  { sku: 'PRSPLY_T2_STND', label: 'T2 Standard', desc: 'RNWY imaged Dell Micro — 14-core / 64GB / 512GB vPro' },
  { sku: 'PRSPLY_ENTERPRISE', label: 'Enterprise', desc: 'RNWY imaged Dell server — quoted per project' },
] as const;

export const hardwareTierLabel = (sku: string): string => {
  const tier = RNWY_HARDWARE_TIERS.find((t) => t.sku === sku);
  return tier ? `${tier.label} (${tier.sku})` : sku;
};

export interface RnwyJobLevel {
  // item 642/651 — branding is a PER-VENUE catalog quantity (custom_branding), entered on each
  // venue; its job-level copy was removed. HA nodes stay job-level — entered once, job-wide (the
  // sheet's "Venue 1 only"). Priced on venue 1 at its ha_node rate; auto-adds SOW scope when > 0.
  haNodes: number; // High-Availability nodes (job-wide, one-time)
  // item 1040 — the Add-hardware pick (see RnwyJobHardware above); absent/null = none chosen
  hardware?: RnwyJobHardware | null;
  // item 1483 (Tom/Chad) — OWNER-FURNISHED PROCESSOR: the owner supplies the processor, so no
  // RNWY box ships. Mutually exclusive with `hardware` (checking it clears the tier pick and vice
  // versa). Spec-only, like the hardware pick — no pricing effect; it prints on the SOW.
  ownerFurnishedProcessor?: boolean;
  // item 789 (Chad) — Third-Party integration QUANTITY (job-level): priced at enrollment as
  // qty × the locked THIRDPARTY-MONTHLY item's unit price. Billed only when the module toggle
  // is ON and qty > 0; toggle-only (qty 0) stays a display flag.
  thirdPartyCount: number;
  misc: RnwyJobMisc[]; // job-level MISC / special line items
  switchPilot: RnwySwitchPilotInputs; // deal-level SwitchPilot switch counts
  modules: RnwyModules; // item 647 — Concourse/Aviator/SwitchPilot include-exclude toggles
  // item 684 (Chad) — PROPERTY-WIDE equipment/sources shared across every venue (entered once). Same
  // shape as the per-venue picks; auto-write into scope once as a "Property-wide" block. Stored verbatim.
  controlledEquipment?: Record<string, string[]>; // category NAME -> selected product names
  sources?: string[]; // individual controllable sources shared across the whole property
  // item 863 (Chad) — the guided pre-venue question: MULTI (Venue 1 auto-named "Head End",
  // sized Medium, then add venues like normal) vs SINGLE (exactly one venue, unnamed, Add
  // venue locked away). Absent on older builds → legacy behavior (unrestricted Add venue).
  venueMode?: 'single' | 'multi';
}

export interface RnwyVenues {
  jobLevel: RnwyJobLevel;
  venues: RnwyVenue[];
}

export const emptySwitchPilot = (): RnwySwitchPilotInputs => ({
  switchCount: 0,
  brand: '',
  specialItems: [],
  specialFeatures: 0,
  initQty: 0,
  perSwitchQty: 0,
  baseLicenseQty: 0,
  tier1Qty: 0,
  tier2Qty: 0,
  tier3Qty: 0,
  tier4Qty: 0,
});

export const switchPilotHasAny = (sp: RnwySwitchPilotInputs): boolean => (
  sp.switchCount !== 0 || sp.specialFeatures !== 0
  || sp.initQty !== 0 || sp.perSwitchQty !== 0 || sp.baseLicenseQty !== 0
  || sp.tier1Qty !== 0 || sp.tier2Qty !== 0 || sp.tier3Qty !== 0 || sp.tier4Qty !== 0
);

export const emptyJobLevel = (): RnwyJobLevel => ({
  haNodes: 0, thirdPartyCount: 0, misc: [], switchPilot: emptySwitchPilot(), modules: emptyModules(), controlledEquipment: {}, sources: [],
});

export const emptyVenue = (systemSizeKey = 'md'): RnwyVenue => ({
  name: '', systemSizeKey, quantities: {}, notes: {}, controlledEquipment: {}, sources: [],
});

export const emptyVenues = (): RnwyVenues => ({ jobLevel: emptyJobLevel(), venues: [] });

// RNWY 2.0 · P4 — the engine's computed result (mirrors basix.da/Models/RnwyEngine.cs), returned
// by the preview endpoint POST proposal/rnwy-preview and rendered as the live 3-option preview.
export interface RnwyEngineOption {
  config: number; graphics: number; dev: number;
  licenseOneTime: number; monthlyLicense: number; retainBuyIn: number; oneTimeSubtotal: number;
}
export interface RnwyEngineHelpDesk { gold: number; platinum: number; titanium: number; }
export interface RnwyEngineSwitchPilot {
  hasSwitchPilot: boolean;
  tier: number; // item 647 — active banded license tier (1–4; 0 = legacy)
  // item 647 — subscription-only; no perpetualLicense/buy-out
  configLabor: number; oneTimeConfig: number; yearlyLicense: number; monthly: number;
}
// item 629 — Crestron pricing (one-time; reads as a standard quote)
export interface RnwyEngineCrestron {
  config: number; graphics: number; dev: number;
  certifiedProgramming: number; certifiedCommissioning: number;
  softwareTotal: number; oneTimeSubtotal: number;
}
// item 1450 — one venue's share of the engine result (mirrors basix.da RnwyVenuePrice)
export interface RnwyEngineVenuePrice {
  name: string; config: number; graphics: number; oneTime: number; perpetualLicense: number; total: number;
}

export interface RnwyEngineResult {
  venueCount: number; totalConfig: number; totalGraphics: number; totalDev: number;
  venues?: RnwyEngineVenuePrice[]; // item 1450 — per-venue breakdown, venue order; absent on old API builds
  helpDesk: RnwyEngineHelpDesk;
  perpetual: RnwyEngineOption; twoYear: RnwyEngineOption; fiveYear: RnwyEngineOption;
  switchPilot: RnwyEngineSwitchPilot; isSwitchPilotOnly: boolean;
  crestron: RnwyEngineCrestron;
}

export const parseVenues = (json: string | null | undefined): RnwyVenues => {
  if (!json || !json.trim()) return emptyVenues();
  try {
    const raw = JSON.parse(json) as Partial<RnwyVenues>;
    const rawJob: Partial<RnwyJobLevel> = raw.jobLevel ?? {};
    return {
      jobLevel: {
        ...emptyJobLevel(),
        ...rawJob,
        // deep-merge the SwitchPilot block so an older venues JSON (no switchPilot) is safe
        switchPilot: { ...emptySwitchPilot(), ...(rawJob.switchPilot ?? {}) },
        // item 647 — deep-merge the module toggles (older JSON has none → defaults)
        modules: { ...emptyModules(), ...(rawJob.modules ?? {}) },
      },
      venues: Array.isArray(raw.venues) ? raw.venues.map((v) => ({ ...emptyVenue(), ...v })) : [],
    };
  } catch {
    return emptyVenues();
  }
};
